'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { CheckCircle2, Expand } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import PostItemActions from './PostItemActions';
import PostItemBody from './PostItemBody';
import PostItemHead from './PostItemHead';
type Props = {
  post: Post;
  isPoliticsParent?: boolean;
  isNsfwParent?: boolean;
  showBranch?: boolean;
  isFullLength: boolean;
  disableLink: boolean;
  spaceSlug?: string;
  currentLevel: number;
  isChild?: boolean;
  uniqueId?: string;
};
const ReplyItem = props => {
  const $ = _c(94);
  const {
    fetchPostReplies
  } = usePostsShared(client, props.uniqueId);
  const router = useRouter();
  const {
    isNsfw,
    isPolitics,
    isTroll
  } = usePostsShared(client);
  const [repliesCollapsed, setRepliesCollapsed] = useState(false);
  const repliesRemaining = props.post.replies_count - (props.post.replies?.length || 0);
  let t0;
  if ($[0] !== isNsfw || $[1] !== props.isNsfwParent || $[2] !== props.post.postFactiiisCount) {
    t0 = isNsfw(props.post.postFactiiisCount) || Boolean(props.isNsfwParent);
    $[0] = isNsfw;
    $[1] = props.isNsfwParent;
    $[2] = props.post.postFactiiisCount;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const [nsfw, setNsfw] = useState(t0);
  let t1;
  if ($[4] !== isPolitics || $[5] !== props.isPoliticsParent || $[6] !== props.post.postFactiiisCount || $[7] !== props.post.space) {
    t1 = isPolitics(props.post.postFactiiisCount, props.post.space) || Boolean(props.isPoliticsParent);
    $[4] = isPolitics;
    $[5] = props.isPoliticsParent;
    $[6] = props.post.postFactiiisCount;
    $[7] = props.post.space;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const [politics, setPolitics] = useState(t1);
  let t2;
  if ($[9] !== isTroll || $[10] !== props.post.postFactiiisCount) {
    t2 = isTroll(props.post.postFactiiisCount);
    $[9] = isTroll;
    $[10] = props.post.postFactiiisCount;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const [troll, setTroll] = useState(t2);
  const mobile = typeof window !== "undefined" && window.innerWidth <= 600;
  let t3;
  if ($[12] !== props.post.id) {
    t3 = hovered => {
      const branches = document.querySelectorAll(`[data-branch-id="${props.post.id}"]`);
      branches.forEach(branch => branch.classList[hovered ? "add" : "remove"]("border-ds-border-hover"));
    };
    $[12] = props.post.id;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleHover = t3;
  let t4;
  if ($[14] !== handleHover || $[15] !== repliesCollapsed) {
    t4 = () => {
      if (repliesCollapsed) {
        handleHover(false);
      }
    };
    $[14] = handleHover;
    $[15] = repliesCollapsed;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== repliesCollapsed) {
    t5 = [repliesCollapsed];
    $[17] = repliesCollapsed;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  const t6 = `reply-${props.post.id}`;
  const t7 = `${props.isChild ? "col-span-2 col-start-2" : ""} relative grid grid-cols-[40px_40px_1fr] gap-x-1 md:grid-cols-[50px_50px_1fr]`;
  const t8 = props.post.triggeredByFactiii ? `/f/${props.post.triggeredByFactiii.slug}` : `/user/${props.post.user.username}`;
  let t9;
  if ($[19] !== props.post.triggeredByFactiii || $[20] !== props.post.user) {
    t9 = props.post.triggeredByFactiii ? <Avatar factiiiAvatarKey={props.post.triggeredByFactiii.avatarKey} factiiiId={props.post.triggeredByFactiii.id} size={35} className="relative z-20 mt-2 max-h-[35px] max-w-[35px] object-cover object-top" /> : <Avatar robohash={props.post.user.robohash} avatarKey={props.post.user.avatarKey} isBot={props.post.user.tag === "BOT"} size={35} className="relative z-20 mt-2 max-h-[35px] max-w-[35px] rounded-full object-cover object-top" userId={props.post.user.id} isAnonymous={props.post.user.anonymous} />;
    $[19] = props.post.triggeredByFactiii;
    $[20] = props.post.user;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <Link onClick={_temp} href={t8}>{t9}</Link>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== handleHover || $[26] !== props.post.id || $[27] !== props.post.replies_count || $[28] !== repliesCollapsed) {
    t11 = props.post.replies_count > 0 && <div className="mt-2 h-full cursor-pointer border-2 border-ds-border-subtle" data-branch-id={props.post.id} onClick={() => setRepliesCollapsed(!repliesCollapsed)} onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)} />;
    $[25] = handleHover;
    $[26] = props.post.id;
    $[27] = props.post.replies_count;
    $[28] = repliesCollapsed;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== repliesCollapsed) {
    t12 = repliesCollapsed && <div className="right-5 mb-2 cursor-pointer rounded-full border-2 border-ds-border-subtle bg-transparent p-2" onClick={() => setRepliesCollapsed(false)}><Expand height={16} width={16} /></div>;
    $[30] = repliesCollapsed;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12) {
    t13 = <div className="col-start-1 flex flex-col items-center">{t10}{t11}{t12}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== props.post.id || $[37] !== props.post.isPrivate || $[38] !== props.post.slug || $[39] !== router) {
    t14 = e_0 => {
      if (!(e_0.target as HTMLElement).closest("#inputId")) {
        const postUrl = props.post.isPrivate ? `/private-post/${props.post.id}` : props.post.slug ? `/post/${props.post.slug}` : `/post/${props.post.id}`;
        router.push(postUrl);
      }
    };
    $[36] = props.post.id;
    $[37] = props.post.isPrivate;
    $[38] = props.post.slug;
    $[39] = router;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== props.post.isTopAnswer) {
    t15 = props.post.isTopAnswer && <div className="mb-1 flex items-center gap-1.5 text-[11px] font-emphasis text-green-500"><CheckCircle2 size={13} />Top answer</div>;
    $[41] = props.post.isTopAnswer;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== nsfw || $[44] !== politics || $[45] !== props.post || $[46] !== props.spaceSlug || $[47] !== props.uniqueId || $[48] !== troll) {
    t16 = <PostItemHead spaceSlug={props.spaceSlug} post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} uniqueId={props.uniqueId} isReply={true} />;
    $[43] = nsfw;
    $[44] = politics;
    $[45] = props.post;
    $[46] = props.spaceSlug;
    $[47] = props.uniqueId;
    $[48] = troll;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [];
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== nsfw || $[52] !== politics || $[53] !== props.disableLink || $[54] !== props.isFullLength || $[55] !== props.post || $[56] !== troll) {
    t18 = <PostItemBody isFullLength={props.isFullLength} post={props.post} politics={politics} nsfw={nsfw} disableLink={props.disableLink} troll={troll} isInView={true} ignorePostFactiiiIds={t17} />;
    $[51] = nsfw;
    $[52] = politics;
    $[53] = props.disableLink;
    $[54] = props.isFullLength;
    $[55] = props.post;
    $[56] = troll;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t15 || $[59] !== t16 || $[60] !== t18) {
    t19 = <div className="rounded-xl bg-background-secondary px-3 py-2">{t15}{t16}{t18}</div>;
    $[58] = t15;
    $[59] = t16;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const t20 = props.currentLevel > (mobile ? 1 : 4);
  let t21;
  if ($[62] !== nsfw || $[63] !== politics || $[64] !== props.post || $[65] !== props.uniqueId || $[66] !== t20 || $[67] !== troll) {
    t21 = <div className="pt-1"><PostItemActions post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} isReply={true} redirectOnReply={t20} uniqueId={props.uniqueId} /></div>;
    $[62] = nsfw;
    $[63] = politics;
    $[64] = props.post;
    $[65] = props.uniqueId;
    $[66] = t20;
    $[67] = troll;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== t14 || $[70] !== t19 || $[71] !== t21) {
    t22 = <div className="col-span-2 col-start-2 py-2" onClick={t14}>{t19}{t21}</div>;
    $[69] = t14;
    $[70] = t19;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== handleHover || $[74] !== nsfw || $[75] !== politics || $[76] !== props || $[77] !== repliesCollapsed) {
    t23 = !repliesCollapsed && props.post.replies?.map((reply, i) => <React.Fragment key={`${reply.id}-${i}`}><div className="relative col-start-1 flex justify-center"><div className="absolute mt-2 size-10 -translate-y-1/2 translate-x-[45%] cursor-pointer rounded-bl-2xl border-b-4 border-l-4 border-ds-border-subtle" data-branch-id={props.post.id} onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)} onClick={() => setRepliesCollapsed(!repliesCollapsed)} />{i < props.post.replies_count - 1 && <div className="h-full cursor-pointer border-2 border-ds-border-subtle" data-branch-id={props.post.id} onClick={() => setRepliesCollapsed(!repliesCollapsed)} onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)} />}</div><ReplyItem {...props} post={reply} isChild={true} isPoliticsParent={politics} isNsfwParent={nsfw} currentLevel={props.currentLevel + 1} showBranch={i !== props.post.replies_count - 1} isFullLength={false} /></React.Fragment>);
    $[73] = handleHover;
    $[74] = nsfw;
    $[75] = politics;
    $[76] = props;
    $[77] = repliesCollapsed;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== fetchPostReplies || $[80] !== props.currentLevel || $[81] !== props.post.id || $[82] !== props.post.isPrivate || $[83] !== props.post.replies || $[84] !== repliesCollapsed || $[85] !== repliesRemaining) {
    t24 = !repliesCollapsed && props.post.replies && repliesRemaining > 0 && <><div className="col-start-1 flex justify-end"><div className="mr-[1.5px] size-1/2 rounded-bl-3xl border-b-4 border-l-4 border-ds-border-subtle" data-branch-id={props.post.id} /></div>{props.currentLevel < (mobile ? 1 : 4) ? <button onClick={() => fetchPostReplies(props.post.id)} type="button" className="col-span-2 col-start-2 -mt-2 text-left text-textSecondary/50">Load {repliesRemaining} More{" "}{repliesRemaining === 1 ? "Reply" : "Replies"}</button> : <Link href={props.post.isPrivate ? `/private-post/${props.post.id}` : `/post/${props.post.id}`} className="col-span-2 col-start-2 -mt-2 text-left text-textSecondary/50"><p className="text-textSecondary/50">View More Replies</p></Link>}</>;
    $[79] = fetchPostReplies;
    $[80] = props.currentLevel;
    $[81] = props.post.id;
    $[82] = props.post.isPrivate;
    $[83] = props.post.replies;
    $[84] = repliesCollapsed;
    $[85] = repliesRemaining;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== t13 || $[88] !== t22 || $[89] !== t23 || $[90] !== t24 || $[91] !== t6 || $[92] !== t7) {
    t25 = <div id={t6} className={t7}>{t13}{t22}{t23}{t24}</div>;
    $[87] = t13;
    $[88] = t22;
    $[89] = t23;
    $[90] = t24;
    $[91] = t6;
    $[92] = t7;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  return t25;
};
export default ReplyItem;
function _temp(e) {
  return e.stopPropagation();
}